import { type LatLng, latLng } from "leaflet";

import Converter from "./converter";

interface FixtureDimensions {
    width: number;
    length: number;
}

interface DirectionModifier {
    horizontal: number;
    vertical: number;
}

/**
 * Resolves which fixture dimension governs spacing along a given direction.
 *
 * @param direction - The direction the array extends in.
 * @returns The dimension property name to offset by.
 */
export const getTypeByDirection = (direction: string) =>
    direction === "up" || direction === "down" ? "length" : "width";

/**
 * Resolves the horizontal and vertical unit modifiers for a given direction.
 *
 * @param direction - The direction the array extends in.
 * @returns The unit modifiers to multiply the offset by.
 */
export const getModifierByDirection = (
    direction: string,
): DirectionModifier => {
    switch (direction) {
        case "right": {
            return {
                horizontal: 1,
                vertical: 0,
            };
        }
        case "left": {
            return {
                horizontal: -1,
                vertical: 0,
            };
        }
        case "down": {
            return {
                horizontal: 0,
                vertical: -1,
            };
        }
        default: {
            return {
                horizontal: 0,
                vertical: 1,
            };
        }
    }
};

/**
 * Calculates evenly spaced positions for an array of lights extending from a center point.
 *
 * @param centerPoint - The position of the first light in the array.
 * @param quantity - The number of lights to place.
 * @param direction - The direction the array extends in.
 * @param spacing - The gap between fixtures, in feet.
 * @param type - The fixture dimensions used to offset each position.
 * @returns The position of each light in the array.
 */
export const getLightArrayPositions = (
    centerPoint: LatLng,
    quantity: number,
    direction: string,
    spacing: number | string,
    type: FixtureDimensions,
): LatLng[] => {
    const positions: LatLng[] = [];
    const modifier = getModifierByDirection(direction);
    const typeProp = getTypeByDirection(direction);
    const spacingInMeters = Converter.convertFeetToMeters(
        parseFloat(String(spacing)),
    );

    for (let light = 0; light < quantity; light++) {
        const longitudeModifier = modifier.horizontal
            ? light * (spacingInMeters + type[typeProp]) * modifier.horizontal
            : 0;

        const latitudeModifier = modifier.vertical
            ? light * (spacingInMeters + type[typeProp]) * modifier.vertical
            : 0;

        const longitude = centerPoint.lng + longitudeModifier;
        const latitude = centerPoint.lat + latitudeModifier;

        positions.push(latLng(latitude, longitude));
    }

    return positions;
};
